import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Divider, Flex, HStack, VStack, Tag, Text, Button, IconButton, Spinner, useDisclosure, Icon,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Tabs, TabList, TabPanels, Tab, TabPanel,
} from '@chakra-ui/react';
import { LuKey, LuEye, LuEyeOff, LuCopy, LuCheck } from 'react-icons/lu';
import { docKey } from '../sdkDocTypes';
import { fetchWrapper } from '../../../helpers';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-gradle';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-dart';
import 'prismjs/themes/prism-tomorrow.css';

// Quill's code-block format has no concept of a language — every snippet comes back as a
// plain <pre class="ql-syntax"> with no language- class for Prism to key off. Guess from
// content shape instead of leaving every block unhighlighted.
const detectLanguage = (code) => {
    const c = code.trim();
    if (c.startsWith('<') && /android:|xmlns:|<manifest|<application|<activity|<service|<uses-permission/.test(c)) return 'markup';
    if (/dependencyResolutionManagement|mavenCentral\(\)|allprojects\s*\{|buildscript\s*\{|jitpack\.io/.test(c)) return 'gradle';
    if (/(?:^import\s+UIKit)|(?:@objc)|(?:guard\s+let)|(?:DispatchQueue)|(?:func\s+\w+\()/.test(c)) return 'swift';
    if (/(?:await\s+Xpush)|(?:Future<)|(?:void\s+main\(\))|(?:^import\s+'package:)/.test(c)) return 'dart';
    if (/(?:^import\s+com\.)|(?:^package\s+com\.)|(?:\bfun\s+\w+\()|(?:\bval\s+\w+\s*=)|(?:override\s+fun)/.test(c)) return 'kotlin';
    if (/(?:^import\s+java\.)|(?:public\s+class)|(?:private\s+\w+\()/.test(c)) return 'java';
    if (/(?:^(?:const|let|var)\s+\w+)|(?:=>\s*\{)|(?:require\()|(?:import\s+.*from\s+['"])/.test(c)) return 'javascript';
    return 'clike';
};

// Renders the rich-text HTML that comes back from the SDK Config API (admin-authored via
// Quill). Code snippets arrive as bare <pre> blocks with no class/language info — after the
// HTML is injected, each one gets wrapped in a Prism-recognizable <code class="language-x">
// and highlighted, since Quill never records which language a snippet was written in.
const RichDoc = ({ html }) => {
    const ref = useRef(null);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;

        // quill.getSemanticHTML() (what RichTextEditor.jsx actually saves) exports every
        // code block as a bare <pre> with no class/attribute — there's no "ql-syntax" or
        // "data-language" shape to match here, just a plain <pre>.
        container.querySelectorAll('pre').forEach(pre => {
            const code = pre.textContent;
            const lang = detectLanguage(code);
            const codeEl = document.createElement('code');
            codeEl.className = `language-${lang}`;
            codeEl.textContent = code;
            pre.innerHTML = '';
            pre.appendChild(codeEl);
            Prism.highlightElement(codeEl);
        });

        // Quill can't nest a list inside a code block, so a list interrupted by a code
        // block splits into separate sibling <ol> elements that each restart at 1. Give
        // every <ol> that continues a preceding one (only broken by non-list content, not
        // by a heading starting a new section) a `start` so numbering carries through.
        let runningCount = 0;
        Array.from(container.children).forEach(child => {
            if (/^H[1-6]$/.test(child.tagName)) {
                runningCount = 0;
                return;
            }
            if (child.tagName === 'OL') {
                if (runningCount > 0) child.setAttribute('start', String(runningCount + 1));
                runningCount += child.querySelectorAll('li').length;
            }
        });

        // A plain hyphen is itself a line-break opportunity (UAX #14), so prose like
        // "step-by-step" or "In-App" can split across lines even under normal wrapping.
        // Swap it for a non-breaking hyphen in text content so the word stays whole
        // unless it truly doesn't fit, without touching code blocks or markup/attributes.
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => node.parentElement?.closest('pre, code')
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
        });
        const textNodes = [];
        let node;
        // eslint-disable-next-line no-cond-assign
        while ((node = walker.nextNode())) textNodes.push(node);
        textNodes.forEach(n => {
            if (n.nodeValue.includes('-')) {
                n.nodeValue = n.nodeValue.replace(/(\w)-(\w)/g, '$1‑$2');
            }
        });
    }, [html]);

    return (
        <Box
            ref={ref}
            sx={{
                h1: { fontSize: '2xl', fontWeight: 'semibold', mt: 5, mb: 2 },
                h2: { fontSize: 'xl', fontWeight: 'semibold', mt: 5, mb: 2 },
                h3: { fontSize: 'lg', fontWeight: 'semibold', mt: 5, mb: 2 },
                p: { color: 'gray.700', mb: 2, lineHeight: 1.7 },
                blockquote: {
                    borderLeft: '3px solid',
                    borderColor: 'gray.300',
                    color: 'gray.600',
                    pl: 4,
                    ml: 0,
                    my: 2,
                },
                pre: {
                    background: 'black',
                    padding: '15px',
                    borderRadius: '8px',
                    overflowX: 'auto',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre',
                },
                'pre code': { background: 'transparent', padding: 0, color: '#f8f8f2' },
                code: { background: 'gray.100', px: '4px', borderRadius: '4px', fontSize: '0.9em' },
                'ul, ol': { paddingInlineStart: '1.5em', color: 'gray.700', mb: 2 },
                '.ql-size-large': { fontSize: '1.5em' },
                '.ql-size-huge': { fontSize: '2.5em' },
            }}
            dangerouslySetInnerHTML={{ __html: html || '' }}
        />
    );
};

// Which `key_type` to request from /customer/get-key for each documentation type.
const KEY_FIELD_BY_TYPE = {
    1: { keyType: 'sdk_key', label: 'SDK Key' },
    2: { keyType: 'event_key', label: 'Event Key' },
    3: { keyType: 'story_key', label: 'Stories Key' },
};

// A fixed run of bullets regardless of the real key's length — mirroring the actual
// length (one bullet per hidden character) made long keys (UUIDs, etc.) overflow the
// modal and force a horizontal scrollbar just to show the masked state.
const maskKey = (key) => {
    if (!key) return '';
    if (key.length <= 8) return '•'.repeat(key.length);
    return `${key.slice(0, 4)}${'•'.repeat(8)}${key.slice(-4)}`;
};

const PlatformKeys = ({ typeId }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [revealed, setRevealed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [keyValue, setKeyValue] = useState('');
    const [copied, setCopied] = useState(false);

    const keyInfo = KEY_FIELD_BY_TYPE[Number(typeId)] || KEY_FIELD_BY_TYPE[1];

    const handleOpen = async () => {
        onOpen();
        setKeyValue('');
        setLoading(true);
        try {
            const res = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/customer/get-key?key_type=${keyInfo.keyType}`);
            setKeyValue(res?.key || '');
        } catch (error) {
            console.error('Error fetching customer keys:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        setRevealed(false);
    };

    const handleCopy = () => {
        if (!keyValue) return;
        navigator.clipboard?.writeText(keyValue);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                color="purple.600"
                px={2}
                borderRadius="md"
                onClick={handleOpen}
                _hover={{ bg: 'purple.600', color: 'white' }}
            >
                View Keys
            </Button>

            <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
                <ModalOverlay />
                <ModalContent borderRadius="14px">
                    <ModalHeader borderBottom="1px" borderColor="gray.100" pb={3}>
                        <HStack spacing={2}>
                            <Box
                                display="inline-flex" alignItems="center" justifyContent="center"
                                w="28px" h="28px" borderRadius="full"
                                bg="purple.50" border="1px solid" borderColor="purple.200"
                            >
                                <Icon as={LuKey} color="purple.500" boxSize={3.5} />
                            </Box>
                            <Text fontSize="md" fontWeight="600" color="gray.800">{keyInfo.label}</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton top={3} />
                    <ModalBody py={4}>
                        {loading ? (
                            <Flex justify="center" py={6}>
                                <VStack spacing={2}>
                                    <Spinner size="sm" color="purple.500" />
                                    <Text fontSize="xs" color="gray.500">Loading key…</Text>
                                </VStack>
                            </Flex>
                        ) : keyValue ? (
                            <>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                    Use this key to authenticate SDK requests.
                                </Text>
                                <Flex align="center" gap={2} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                                    <Text
                                        flex="1"
                                        minW="0"
                                        fontFamily="mono"
                                        fontSize="sm"
                                        color="gray.800"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                    >
                                        {revealed ? keyValue : maskKey(keyValue)}
                                    </Text>
                                    <HStack spacing={1} flexShrink={0}>
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            aria-label={revealed ? 'Hide key' : 'Reveal key'}
                                            icon={<Icon as={revealed ? LuEyeOff : LuEye} boxSize={4} />}
                                            onClick={() => setRevealed(v => !v)}
                                        />
                                        <IconButton
                                            size="sm"
                                            variant="ghost"
                                            color={copied ? 'green.500' : undefined}
                                            aria-label={copied ? 'Copied' : 'Copy key'}
                                            icon={<Icon as={copied ? LuCheck : LuCopy} boxSize={4} />}
                                            onClick={handleCopy}
                                        />
                                    </HStack>
                                </Flex>
                                <Text fontSize="xs" color="orange.600" mt={3}>
                                    Keep this key secret.
                                </Text>
                            </>
                        ) : (
                            <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                                No key found.
                            </Text>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px" borderColor="gray.100">
                        <Button size="sm" onClick={handleClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

const PlatformDoc = ({ platform }) => {
    const hasVariants = (platform.variants || []).length > 0;

    return (
        <Box>
            <Flex justifyContent="space-between" alignItems="center">
                <Text fontSize="2xl" fontWeight="semibold">
                    {platform.heading || `${platform.label} SDK Integration`}
                </Text>
                <HStack spacing={3}>
                    <PlatformKeys typeId={platform.type_id} />
                    <Tag bg={platform.tag_color || 'gray.500'} color="white" fontSize="xs">{platform.label}</Tag>
                </HStack>
            </Flex>
            <Divider my={2} />

            {platform.description && <RichDoc html={platform.description} />}

            {platform.version && (
                <Text color="gray.600" mb={4}>
                    <strong>{platform.label} Version:</strong> {platform.version} or higher
                </Text>
            )}

            {platform.docs?.[docKey(null)] && <RichDoc html={platform.docs[docKey(null)]} />}

            {hasVariants && (
                <Tabs variant="enclosed" mt={4}>
                    <TabList bg="#f1f5f9" p="1" borderRadius="full" display="inline-flex" mb={4}>
                        {platform.variants.map(v => (
                            <Tab
                                key={v.id}
                                _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                                borderRadius="full"
                                fontSize="sm"
                            >
                                {v.label}
                            </Tab>
                        ))}
                    </TabList>

                    <TabPanels>
                        {platform.variants.map(v => (
                            <TabPanel key={v.id} px={0}>
                                <RichDoc html={platform.docs?.[docKey(v.id)] || ''} />
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            )}
        </Box>
    );
};

export default PlatformDoc;
